import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ActuacionContractual,
  CreateActuacionContractualRequest,
  UpdateActuacionContractualRequest
} from '../models/actuacion-contractual.interface';
import { environment } from '../../../../environments/environment';
import { ApiEnvelope } from '../../../shared/models/api-envelope';
import { ActuacionContractualMapper } from '../models/actuacion-contractual.mapper';
import { ActuacionContractualDto } from '../models/actuacion-contractual.dto';

@Injectable({
  providedIn: 'root'
})
export class ActuacionContractualService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.baseUrl.generic}/Actuacion`;

  private unwrapResponse<T>() {
    return map((response: T | ApiEnvelope<T>) => {
      const maybe = response as ApiEnvelope<T>;
      return maybe && (maybe as { data?: T }).data !== undefined
        ? (maybe.data as T)
        : (response as T);
    });
  }

  private normalizeActuacionContractual(raw: ActuacionContractualDto): ActuacionContractual {
    return ActuacionContractualMapper.fromDto(raw);
  }

  private normalizeList(
    list: ActuacionContractualDto[] | ActuacionContractual[]
  ): ActuacionContractual[] {
    return ActuacionContractualMapper.fromDtoList(list as ActuacionContractualDto[]);
  }

  /**
   * Obtiene todas las actuaciones contractuales
   */
  getActuacionesContractuales(): Observable<ActuacionContractual[]> {
    return this.http.get<ApiEnvelope<ActuacionContractualDto[]>>(`${this.baseUrl}/GetAll`).pipe(
      this.unwrapResponse<ActuacionContractualDto[]>(),
      map((res: ActuacionContractualDto[]) => this.normalizeList(res))
    );
  }

  /**
   * Obtiene una actuación contractual por ID
   */
  getActuacionContractualById(id: number): Observable<ActuacionContractual> {
    const params = new HttpParams().set('id', id.toString());
    return this.http
      .get<ApiEnvelope<ActuacionContractualDto>>(`${this.baseUrl}/GetByID`, { params })
      .pipe(
        this.unwrapResponse<ActuacionContractualDto>(),
        map((res: ActuacionContractualDto) => this.normalizeActuacionContractual(res))
      );
  }

  /**
   * Crea una nueva actuación contractual
   */
  createActuacionContractual(
    actuacionContractual: CreateActuacionContractualRequest
  ): Observable<ActuacionContractual> {
    return this.http
      .post<ApiEnvelope<ActuacionContractualDto>>(`${this.baseUrl}/Create`, actuacionContractual)
      .pipe(
        this.unwrapResponse<ActuacionContractualDto>(),
        map((res: ActuacionContractualDto) => this.normalizeActuacionContractual(res))
      );
  }

  /**
   * Actualiza una actuación contractual existente
   */
  updateActuacionContractual(
    id: number,
    actuacionContractual: UpdateActuacionContractualRequest
  ): Observable<boolean> {
    return this.http
      .put<ApiEnvelope<boolean>>(`${this.baseUrl}/${id}`, actuacionContractual)
      .pipe(this.unwrapResponse<boolean>());
  }

  /**
   * Cambia el estado de una actuación contractual
   */
  toggleActuacionContractualStatus(id: number, isActive: boolean): Observable<boolean> {
    return this.http
      .patch<ApiEnvelope<boolean>>(`${this.baseUrl}/${id}/${isActive}`, {})
      .pipe(this.unwrapResponse<boolean>());
  }
}
